import React, { useMemo, useState } from "react";
import { Alert, Button, Form, Modal, Spinner } from "react-bootstrap";
import { FaEnvelope, FaPaperPlane } from "react-icons/fa";
import { WEB3FORMS_CONFIG } from "../../contactConfig";
import { useTranslation } from "../../i18n";

const initialForm = {
  name: "",
  email: "",
  company: "",
  inquiryType: "working_student",
  message: "",
};

function ContactModal({ show, onHide }) {
  const { t } = useTranslation();
  const inquiryOptions = t("contactForm.inquiryOptions");
  const [form, setForm] = useState(initialForm);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const isValid = useMemo(() => {
    return (
      form.name.trim().length >= 2 &&
      /\S+@\S+\.\S+/.test(form.email) &&
      form.message.trim().length >= 12
    );
  }, [form]);

  const updateField = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const resetAndClose = () => {
    setForm(initialForm);
    setStatus("idle");
    setError("");
    onHide();
  };

  const sendEmail = async (event) => {
    event.preventDefault();
    setError("");

    if (!isValid) {
      setError(t("contactForm.validationError"));
      return;
    }

    setStatus("sending");

    try {
      const formData = new FormData();
      formData.append("access_key", WEB3FORMS_CONFIG.accessKey);
      formData.append("subject", `Portfolio inquiry from ${form.name}`);
      formData.append("from_name", "Lokesh Khandelwal Portfolio");
      formData.append("name", form.name);
      formData.append("email", form.email);
      formData.append("replyto", form.email);
      formData.append("company", form.company || "Not provided");
      formData.append("inquiry_type", form.inquiryType);
      formData.append("message", form.message);
      formData.append("to_email", WEB3FORMS_CONFIG.toEmail);
      formData.append("source", "Portfolio contact form");

      const response = await fetch(WEB3FORMS_CONFIG.endpoint, {
        method: "POST",
        body: formData,
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.message || "Web3Forms request failed");
      }

      setStatus("sent");
      setForm(initialForm);
    } catch (sendError) {
      setStatus("idle");
      setError(t("contactForm.sendError"));
    }
  };

  return (
    <Modal show={show} onHide={resetAndClose} centered className="contact-modal">
      <Modal.Header closeButton>
        <Modal.Title>
          <FaEnvelope /> {t("contactForm.title")}
        </Modal.Title>
      </Modal.Header>
      <Form onSubmit={sendEmail}>
        <Modal.Body>
          <p className="contact-modal-subtitle">{t("contactForm.subtitle")}</p>

          {status === "sent" && (
            <Alert variant="success" className="contact-alert">
              {t("contactForm.success")}
            </Alert>
          )}

          {error && (
            <Alert variant="warning" className="contact-alert">
              {error}
            </Alert>
          )}

          <Form.Group className="mb-3" controlId="contactName">
            <Form.Label>{t("contactForm.name")}</Form.Label>
            <Form.Control
              name="name"
              value={form.name}
              onChange={updateField}
              placeholder={t("contactForm.namePlaceholder")}
              autoComplete="name"
            />
          </Form.Group>

          <Form.Group className="mb-3" controlId="contactEmail">
            <Form.Label>{t("contactForm.email")}</Form.Label>
            <Form.Control
              type="email"
              name="email"
              value={form.email}
              onChange={updateField}
              placeholder={t("contactForm.emailPlaceholder")}
              autoComplete="email"
            />
          </Form.Group>

          <Form.Group className="mb-3" controlId="contactCompany">
            <Form.Label>{t("contactForm.company")}</Form.Label>
            <Form.Control
              name="company"
              value={form.company}
              onChange={updateField}
              placeholder={t("contactForm.companyPlaceholder")}
              autoComplete="organization"
            />
          </Form.Group>

          <Form.Group className="mb-3" controlId="contactInquiry">
            <Form.Label>{t("contactForm.inquiryType")}</Form.Label>
            <Form.Select name="inquiryType" value={form.inquiryType} onChange={updateField}>
              {(Array.isArray(inquiryOptions)
                ? inquiryOptions
                : [
                    { value: "Working Student Opportunity", label: "Working Student Opportunity" },
                    { value: "Recruiter / Hiring", label: "Recruiter / Hiring" },
                    { value: "MEERAJ SRGL / Prototype Discussion", label: "MEERAJ SRGL / Prototype Discussion" },
                    { value: "Project Inquiry", label: "Project Inquiry" },
                  ]
              ).map((option) => (
                <option value={option.value} key={option.value}>
                  {option.label}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group controlId="contactMessage">
            <Form.Label>{t("contactForm.message")}</Form.Label>
            <Form.Control
              as="textarea"
              rows={5}
              name="message"
              value={form.message}
              onChange={updateField}
              placeholder={t("contactForm.messagePlaceholder")}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={resetAndClose} className="contact-cancel-btn">
            {t("contactForm.cancel")}
          </Button>
          <Button type="submit" className="contact-submit-btn" disabled={status === "sending"}>
            {status === "sending" ? (
              <>
                <Spinner animation="border" size="sm" /> {t("contactForm.sending")}
              </>
            ) : (
              <>
                <FaPaperPlane /> {t("contactForm.submit")}
              </>
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

export default ContactModal;
